import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import LogRocket from 'logrocket';
import moment from 'moment';
import { useParams, useLocation, useHistory } from 'react-router-dom';

import { Listing, SubscriptionModalOrigin, SubscriptionPlan } from '../../../interfaces/idashboard';
import { Container } from '@mui/material';
import { DashboardState } from '../../../interfaces/idashboard';
import { DashboardPropTypes, FilterVal, VisibleListingsProp } from '../../../interfaces/iproptypes';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TutorialModal from '../../Modals/TutorialModal';
import UserOnboardingModal from 'src/App/Pages/Register/components/UserOnboardingModal';

import { SettingsDashboard, MyAnalysisDashboard } from './Pages';
import {
  getFilteredListings,
  getVisibleListings,
  isDefaultFilter,
  titleCase,
  makeLowerCase,
  extractVisibleListings,
  getBoundedListings,
  getMarketingTrackers,
  getUniqueUserId,
  extractStaticListings,
  clearApiCache,
  getUserPreferredCurrency,
} from 'src/utils/functions';
import { useHighlight, useOpenOverviewOnboardingPage } from 'src/utils/hooks';
import {
  OnboardingModalMobile,
  SubscriptionModal,
  AuthModal,
  UpgradeSubscriptionModal,
  UpgradeYearlyPlanModal,
} from '../../Modals';
import { Header, Sidebar } from '../../HOC';
import '../Dashboard.scss';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  LIGHT_GREY,
  DARK_GREY,
  BACKGROUND_COLOR,
  DYNAMIC_PRICING_ANALYTICS_MIXPANEL_TOKEN,
  sidebarWidth,
  DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE,
  special_country_code_currency_map,
  sidebarClosedWidth,
  HEADER_HEIGHT,
  countryNameCodeMapFromDB,
} from 'src/const';
import { callApi } from 'src/core/callApi';
import {
  fetchCachedMarketData,
  fetchDummyDataForLockedMarket,
  getDistrictInfo,
  getSubmarketInfo,
  initializeSavedMarketsData,
  setSelectedListLoadingAction,
  setUserCountryCode,
} from 'src/store/Dashboard/actions';
import EmailVerifyModal from 'src/App/Modals/EmailVerifyModal';
import { DynamicPricingDashboard } from '../Pricing';
import postOnSlack from 'src/core/postOnSlack';
import MarketSearchBar from 'src/App/HOC/Header/MarketSearchBar';
import { HomePage } from 'src/App/Pages';
import { timeZoneCCMap } from '../../assets/timeZoneCCMap';

import OverviewLandingPage from 'src/App/Pages/OverviewLandingPage';
import { OverviewPage } from 'src/App/Pages/OverviewPage';
import ViewDemoButton from 'src/App/Components/Buttons/ViewDemoButton';
import AdvancedDashboard from 'src/App/Pages/Advanced/AdvancedDashboard';
import SearchListings from 'src/App/Pages/SearchListings/SearchListings';
import DemoBookingForm from 'src/App/Components/Forms/DemoBookingForm';
import UserDescriptionFormOnGoogleAuth from 'src/App/Modals/UserDescriptionFormOnGoogleAuth';
import { EstimatorPage } from 'src/App/Pages/EstimatorPage';
import { useSubscription } from 'src/hooks/useCustomHooks';
import { useIdentifyAmplitudeUser } from 'src/hooks/useProductAnalytics';
import SearchBarModal from 'src/App/Components/Modals/SearchBarModal';
import IndividualListingPage from 'src/App/Pages/IndividualListingPage';
import IndividualHostPage from 'src/App/Pages/IndividualHostPage';
import SettingsDashboardV2 from 'src/App/Pages/SettingsV2/SettingsDashboardV2';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: BACKGROUND_COLOR,
      boxShadow: `inset 0px 5px 10px 0px ${DARK_GREY}`,
      padding: 0,
      overflow: 'hidden',
    },
    content: {
      maxWidth: 4600,
      padding: 0,
      [theme.breakpoints.up(900)]: {
        marginLeft: sidebarClosedWidth,
        transition: theme.transitions.create('padding', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    contentShift: {
      [theme.breakpoints.up(900)]: {
        marginLeft: theme.spacing(8),
        transition: theme.transitions.create('padding', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
    container: {
      [theme.breakpoints.down(1160)]: {
        paddingTop: theme.spacing(12),
      },
    },
    paperModal: {
      position: 'absolute',
      backgroundColor: BACKGROUND_COLOR,
      padding: theme.spacing(8),
      borderRadius: '70px',
      [theme.breakpoints.up('xs')]: {
        width: 340,
      },
      [theme.breakpoints.up('sm')]: {
        width: 530,
      },
      [theme.breakpoints.up('lg')]: {
        width: 650,
      },
    },
    signupModal: {
      position: 'absolute',
      [theme.breakpoints.up('xs')]: {
        width: 340,
      },
      [theme.breakpoints.up('sm')]: {
        width: 530,
      },
      [theme.breakpoints.up('lg')]: {
        width: 650,
      },
      height: 620,
      backgroundColor: LIGHT_GREY,
      borderRadius: 10,
      padding: theme.spacing(2, 4, 3),
    },
    warningModal: {
      position: 'absolute',
      backgroundColor: BACKGROUND_COLOR,
      padding: theme.spacing(4),
      borderRadius: 10,
      [theme.breakpoints.up('xs')]: {
        width: 300,
      },
    },
    customButton: {
      background: COLOR_PRIMARY_DARK,
      borderRadius: 5,
      border: 70,
      color: 'white',
      height: 45,
      padding: '0 15px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
    },
    customButtonReversed: {
      background: BACKGROUND_COLOR,
      borderRadius: 5,
      border: 70,
      color: COLOR_PRIMARY_DARK,
      height: 45,
      padding: '0 15px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
    },
    creditsText: {
      color: COLOR_PRIMARY,
      fontSize: '1rem',
      cursor: 'pointer',
    },
    iconButton: {
      padding: 3,
    },
    addCreditsBtn: {
      fontSize: 20,
      color: COLOR_PRIMARY_DARK,
      display: 'inline-block',
      marginBottom: '3px',
    },
    SEOLinkStyle: {
      position: 'relative',
      top: -500,
    },
  })
);

// consts
const setMarketingTrackingURL =
  'https://4b5jgzfm05.execute-api.us-east-2.amazonaws.com/default/setMarketingTracking';

const getEstimatorInvestmentCalculatorInputQueries = (searchParams: string) => {
  const propertyPriceQuery = new URLSearchParams(searchParams).get('propertyPrice') || '';
  const managementFeeQuery = new URLSearchParams(searchParams).get('managementFee') || '';
  const propertyTaxQuery = new URLSearchParams(searchParams).get('propertyTax') || '';
  const lodgingTaxQuery = new URLSearchParams(searchParams).get('lodgingTax') || '';
  const mortgageFeesQuery = new URLSearchParams(searchParams).get('mortgageFees') || '';
  const hoaFeesQuery = new URLSearchParams(searchParams).get('hoaFees') || '';
  const maintenanceQuery = new URLSearchParams(searchParams).get('maintenance') || '';
  const insuranceQuery = new URLSearchParams(searchParams).get('insurance') || '';
  const electricityQuery = new URLSearchParams(searchParams).get('electricity') || '';
  const wifiQuery = new URLSearchParams(searchParams).get('wifi') || '';
  const gasQuery = new URLSearchParams(searchParams).get('gas') || '';
  const otherQuery = new URLSearchParams(searchParams).get('other') || '';
  const homeFurnishingsQuery = new URLSearchParams(searchParams).get('homeFurnishings') || '';
  const closingCostsQuery = new URLSearchParams(searchParams).get('closingCosts') || '';
  const currencyQuery = new URLSearchParams(searchParams).get('currency') || '';

  return {
    propertyPriceQuery,
    managementFeeQuery,
    propertyTaxQuery,
    lodgingTaxQuery,
    mortgageFeesQuery,
    hoaFeesQuery,
    maintenanceQuery,
    insuranceQuery,
    electricityQuery,
    wifiQuery,
    gasQuery,
    otherQuery,
    homeFurnishingsQuery,
    closingCostsQuery,
    currencyQuery,
  };
};

const MarketDashboard = ({
  tailoredRegionMarkers,
  search,
  user,
  currency,
  getTokenThunk,
  getUserInfo,
  getTailoredRegionMarkers,
  setCurrency,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  getCachedSearchMarkets,
  cachedSearchMarkets,
  cachedTop10MarketCountryWise,
  getTop10MarketCountryWise,
}: DashboardPropTypes) => {
  const classes = useStyles();
  const isLoggedIn = Boolean(getTokenThunk());
  const location = useLocation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const isMobile = useMediaQuery(theme.breakpoints.down(520));
  const dispatch = useDispatch();
  const history = useHistory();
  useIdentifyAmplitudeUser();

  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [overviewDashboardLayoutView, setOverviewDashboardLayoutView] = useState<
    'dashboard' | 'map' | 'discovery' | 'compare' | 'advance' | 'searchListing' | 'submarkets'
  >('dashboard');

  const [advancedTab, setAdvancedTab] = useState(0);

  const appVisitCount = window.localStorage.getItem('appVisitCount');
  const pathname = window.location.pathname;

  // url params
  let { country, city, district, submarketID, isTailored, settings } = useParams<{
    country: string;
    city: string;
    district: string;
    submarketID: string;
    isTailored: string;
    settings: string;
  }>();

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let loginCountryCode = timeZoneCCMap[userTimeZone] || 'US';

  // get district info
  useEffect(() => {
    if (submarketID) {
      dispatch(getSubmarketInfo(submarketID, loginCountryCode));
    } else if (country && city && district) {
      setIsFilterChange(false);

      const countryCode = countryNameCodeMapFromDB[country] || null;
      if (country == '0' || !countryCode) {
        // Fetch district info first and then cached market data with the right country code
        dispatch(getDistrictInfo(loginCountryCode, district, city, country));
      } else {
        // queue both the api calls asynchronously to save time
        dispatch(getDistrictInfo(loginCountryCode, district, city, country, false));
        dispatch(fetchCachedMarketData(district, city, countryCode, country, loginCountryCode));
      }
    }

    if (settings && settings === 'settings') {
      handleMarketSidebarClick('settings');
    }
  }, []);

  useEffect(() => {
    if (!appVisitCount) {
      window.localStorage.setItem('appVisitCount', String(1));
    } else {
      window.localStorage.setItem('appVisitCount', String(Number(appVisitCount) + 1));
    }
  }, []);

  useEffect(() => {
    let tailorRegionName = window.localStorage.getItem('tailoredRegion');

    if (tailorRegionName && isLoggedIn) {
      history.push(`/airbnb-data/0/0/${tailorRegionName}`);
      window.location.reload();
      localStorage.removeItem('tailoredRegion');
    }
  }, []);

  useEffect(() => {
    if (!Intl || !Intl.DateTimeFormat) {
      console.log('Intl API is not available in this environment.');
      dispatch(setUserCountryCode('US'));
    }
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log(
      'User Time Zone: ',
      userTimeZone,
      ' and Country Code: ',
      timeZoneCCMap[userTimeZone]
    );

    const countryCode = timeZoneCCMap[userTimeZone] || 'US';

    if (special_country_code_currency_map[countryCode]) {
      setCurrency(getUserPreferredCurrency(special_country_code_currency_map[countryCode]));
    }

    dispatch(setUserCountryCode(countryCode));
  }, [Intl]);

  useEffect(() => {
    let userNameFromLocalstorage = window.localStorage.getItem('user-name');
    let userEmailFromLocalstorage = window.localStorage.getItem('user-email');

    if (user.email !== 'Trial' && user.email !== userEmailFromLocalstorage) {
      // Checking if the email from the localstorage and current emails are same or not
      //if not, updating the localstorage
      window.localStorage.setItem('user-email', user.email);
      userEmailFromLocalstorage = user.email;
    }

    if (user.userName !== 'Trial' && user.userName !== userNameFromLocalstorage) {
      // Checking if the name from the localstorage and current name are same or not
      //if not, updating the localstorage
      window.localStorage.setItem('user-name', user.userName);
      userNameFromLocalstorage = user.userName;
    }

    if ((window as any).Tawk_API) {
      (window as any).Tawk_API.onLoad = () => {
        (window as any).Tawk_API.setAttributes(
          {
            name: userNameFromLocalstorage,
            email: userEmailFromLocalstorage,
          },
          error => {
            console.error('Tawk API Error:', error);
          }
        );
        (window as any).Tawk_API.hideWidget();
      };
    }
  }, [user.email]);

  // query params
  const searchParams = location.search;
  const langQuery = new URLSearchParams(searchParams).get('lang') || '';
  const pageQuery = new URLSearchParams(searchParams).get('page') || '';
  const typeQuery = new URLSearchParams(searchParams).get('type') || '';
  const regionQuery = new URLSearchParams(searchParams).get('region') || '';
  const affiliatedByQuery = new URLSearchParams(searchParams).get('affiliatedBy') || '-1';

  const isOverviewLanding = pathname === '/airbnb-data/landing';
  const isIndividualLandingPage = pathname.includes('/listing/');
  const isIndividualHostPage = pathname.includes('/host/');

  const referrer = document.referrer;
  const [langTracker, pageTracker, typeTracker, regionTracker] = getMarketingTrackers();
  if (!langTracker && !pageTracker && !typeTracker && !regionTracker) {
    localStorage.setItem(
      'marketing-trackers',
      JSON.stringify({
        lang: langQuery,
        page: pageQuery ? pageQuery : referrer,
        type: typeQuery,
        region: regionQuery,
      })
    );
  }

  // legacy estimator query
  const estimatorAddressQuery = new URLSearchParams(searchParams).get('estimatorAddress');

  // new estimator queries
  const estimatorAddressFullQuery = new URLSearchParams(searchParams).get('address-full');
  const estimatorAddressFshortQuery = new URLSearchParams(searchParams).get('address-short');
  const estimatorBedroomsQuery = new URLSearchParams(searchParams).get('bedrooms');
  const estimatorReportTypeQuery = new URLSearchParams(searchParams).get('report-type');
  const estimatorPropertyPriceQuery = new URLSearchParams(searchParams).get('property-price');
  const estimatorMontlyRentQuery = new URLSearchParams(searchParams).get('monthly-rent');
  const dataStoreTimePeriodQuery = new URLSearchParams(searchParams).get(
    'listing-export-time-period'
  );
  const dataStoreTimeGranularityQuery = new URLSearchParams(searchParams).get(
    'listing-export-granularity'
  );

  useEffect(() => {
    if (search.data && search.data.currency) {
      setCurrency(getUserPreferredCurrency(search.data.currency));
    }
  }, [search.data]);

  // declarations
  const [landing, setLanding] = useState('investing');
  const states = require('us-state-codes');
  const cityName =
    search.data.countryCode == 'US' || country == 'united-states'
      ? states.getStateNameByStateCode(city)
      : search.data.city;

  const [isFilterChange, setIsFilterChange] = useState(true);
  const [dashboardState, setdashboardState] = React.useState({
    showInsightsDashboard: true,
    showEstimatorDashboard: false,
    showMyAnalysisDashboard: false,
    showSettingsDashboard: false,
    showDynamicPricingDashboard: false,
    showTailoredRegion: false,
    showAdvancedDashboard: false,
    showSearchListingDashboard: false,
  } as DashboardState);
  const isHomePage = window.location.pathname === '/';

  // Redirect user to dummy market dashboard if hitting '/
  if (isHomePage) {
    // this below if condition is to keep track that user came from which affiliated partner
    if (affiliatedByQuery !== '-1') {
      localStorage.setItem('affiliatedKey', affiliatedByQuery);
    }
    window.location.href = window.location.origin + '/airbnb-data';
  }

  const [selectedSidebarIndex, setSelectedSidebarIndex] = useState(
    window.location.pathname === '/' ? -1 : 0
  );
  let isAdmin = user && user.role && user.role === 'admin' ? true : false;
  const [openAlert, setOpenAlert] = React.useState(true);
  const [isTailoredRegionOpen, setIsTailoredRegionOpen] = React.useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const {
    subscriptionModalOpen,
    setSubscriptionModalOpen,
    requiredSubscriptionPlan,
    setRequiredSubscriptionPlan,
    subscriptionModalView,
    setSubscriptionModalView,
    subscriptionModalOrigin,
    setSubscriptionModalOrigin,
    showUpgradeSubscriptionModal,
    setShowUpgradeSubscriptionModal,
    showSearchCreditDeductWarningModal,
    setShowSearchCreditDeductWarningModal,
    handleOnSampleClick,
    showTrial,
    setShowTrial,
    resetSubscriptionModal,
  } = useSubscription(isLoggedIn, setLoginModalOpen);

  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [isEmailVerifyModalOpen, setIsEmailVerifyModalOpen] = useState<boolean>(false);
  const [isUserVerified, setIsUserVerified] = useState<boolean>(true);
  const [rateLimitAlert, setRateLimitAlert] = useState<boolean>(false);

  const [showDemoBookingForm, setShowDemoBookingForm] = useState<boolean>(false);
  const [demoFormHeadingFor, setDemoFormHeadingFor] = useState<string>('demo');
  const [searchBaseCC, setSearchBaseCC] = useState(loginCountryCode);

  const [showUpgradeYearlySubscriptionModal, setShowUpgradeYearlySubscriptionModal] = useState<
    boolean
  >(false);

  const dummyMarket =
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE[loginCountryCode]?.overview ||
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE['US']?.overview;

  // Edge case handled where Dummay data is not present for user signup country So we made US as fallback country
  if (dummyMarket.district == 'Boca Raton') {
    loginCountryCode = 'US';
  }
  const dummyMarketEstimator =
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE[loginCountryCode]?.estimator ||
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE['US']?.estimator;

  // State Variable for Estimator
  const [centerCoord, setCenterCoord] = useState<any>(dummyMarketEstimator?.coordinates);
  const [address, setAddress] = useState<string>('');
  const [prevCenterCoord, setPrevCenterCoord] = useState<any>(dummyMarketEstimator?.coordinates);

  const [openReportComparePage, setOpenReportComparePage] = useState<boolean>(false);

  const isListInitialized = useRef<boolean>(false);

  const cachedMarketData = useSelector(state => (state as any).dashboard.cachedMarketData);

  const openOverviewOnboardingPage = useOpenOverviewOnboardingPage();

  useEffect(() => {
    const savedUserPrefCountryCode = localStorage.getItem('user-pref-country-code') || '';
    setSearchBaseCC(savedUserPrefCountryCode ? savedUserPrefCountryCode : loginCountryCode);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let appVisitCount = localStorage.getItem('appVisitCount');
      let affiliatedKey = localStorage.getItem('affiliatedKey');

      if (appVisitCount === '2') {
        const url =
          'https://ak27jez2w0.execute-api.us-east-2.amazonaws.com/default/affiliatePartnerStats';
        const config = {
          queryParams: {
            operationType: 'update',
            updatedKey: 'visitors_count',
            affiliateKey: affiliatedKey,
          },
        };

        if (affiliatedKey) {
          const res = await callApi(url, config, false);
        }
      }

      clearApiCache();
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (user.isVerified != isUserVerified) {
      setIsUserVerified(user.isVerified);
    }
  }, [user, user.isVerified]);

  // This UseEffect is responsible to show Email verification modal when after user search for 3 markets or after 1hr of signup
  useEffect(() => {
    let totalSearchDoneByUser =
      Number(window.localStorage.getItem('investment-search-attempts')) || 0;
    if (
      isUserVerified === false &&
      totalSearchDoneByUser >= 3 &&
      user.isSubscribed == false &&
      !user.loading
    ) {
      setIsEmailVerifyModalOpen(true);
      setRateLimitAlert(true);
    }
    if (isUserVerified === false && user.isSubscribed == false) {
      setTimeout(() => {
        setIsEmailVerifyModalOpen(true);
      }, 3600000); //1 hr
    }
    if (isUserVerified) {
      setRateLimitAlert(false);
    }
  }, [isUserVerified]);

  // analytics
  useEffect(() => {
    getCachedSearchMarkets();
    getTop10MarketCountryWise();
    checkGoogleAuthUserDescriptionFormStatus();
    if (pageQuery && !isLoggedIn) {
      callApi(
        setMarketingTrackingURL,
        {
          queryParams: {
            page: pageQuery,
          },
        },
        false
      );
    }
  }, []);

  // Tutorial logic
  useEffect(() => {
    // Check for market tutorial when user is on the landing page
    const shouldShowNeighborhoodTutorial =
      localStorage.getItem('show_neighborhood_tutorial') &&
      localStorage.getItem('show_neighborhood_tutorial') === 'true';
    if (shouldShowNeighborhoodTutorial) {
      setTutorialType('neighborhood_research');
      setShowTutorial(true);
      return;
    }
  }, [location.pathname]);

  // Handle tutorial close
  const handleTutorialClose = () => {
    setShowTutorial(false);
  };

  useEffect(() => {
    let isReturningUser = false;
    const timeNow = moment();
    const lastVisit = localStorage.getItem('visit-time');
    if (lastVisit) {
      const lastVisitMoment = moment(lastVisit);
      const diff = timeNow.diff(lastVisitMoment, 'hours');
      isReturningUser = diff > 1;
    } else {
      localStorage.setItem('visit-time', timeNow.toISOString());
    }
    if (
      isLoggedIn &&
      user &&
      user.email &&
      user.userName != 'Trial' &&
      !user.email.includes('@airbtics.com')
    ) {
      LogRocket.init('zqebrc/airbtics');
      LogRocket.identify(user?.email as string, {
        name: user?.userName as string,
        email: user?.email as string,
        isPaid: user.regions ? true : false,
        isReturningUser: isReturningUser,
      });
    }

    if (user && user.email != 'Trial' && user.userName != 'Trial') {
      const uniqueUserId: string | null = getUniqueUserId(user);
      try {
        console.log(
          'Unique User ID: ',
          uniqueUserId,
          ' | Is HotJar Active ? ',
          (window as any).hj ? true : false
        );
        (window as any).hj &&
          (window as any).hj('identify', uniqueUserId, {
            email: user.email,
          });
      } catch (err) {
        console.log('Error while setting up the user email in hotjar: ', err);
      }
    }
  }, [user]);

  // login/signup modal popup logic

  useEffect(() => {
    if (
      !isLoggedIn &&
      !isHomePage &&
      !location.pathname.includes('airbnb-calculator') &&
      !location.pathname.includes('tailored/t')
    ) {
      setLoginModalOpen(true);
    }
  }, [isLoggedIn, dashboardState]);

  useEffect(() => {
    if (isLoggedIn && user.email?.toLowerCase() !== 'trial' && !isListInitialized.current) {
      dispatch(initializeSavedMarketsData());
      isListInitialized.current = true;
    }
    if (!isLoggedIn) {
      dispatch(setSelectedListLoadingAction(false));
    }
  }, [user, isLoggedIn]);

  // api calls
  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  // route checks
  useEffect(() => {
    if (window.location.pathname.includes('my-analysis')) {
      setLanding('my-analysis');
      handleMarketSidebarClick('myAnalysis');
      handleSidebarListItemClick(5);
    }
    if (window.location.pathname.includes('airbnb-calculator')) {
      setLanding('estimator');
      handleMarketSidebarClick('estimator');
      handleSidebarListItemClick(1);
    }
    if (window.location.pathname.includes('markets/compare')) {
      setOverviewDashboardLayoutView('compare');
      handleSidebarListItemClick(2);
    }
    if (window.location.pathname.includes('pricing')) {
      setLanding('dynamic-pricing');
      handleMarketSidebarClick('listings');
      handleSidebarListItemClick(7);
    }
    if (window.location.pathname.includes('settings')) {
      setLanding('settings');
      handleMarketSidebarClick('settings');
      handleSidebarListItemClick(10);
    }
    if (window.location.pathname.includes('/tailored/t')) {
      setLanding('tailoredRegion');
      handleMarketSidebarClick('tailoredRegion');
      handleSidebarListItemClick(11);
    }
    if (window.location.pathname.includes('advanced-dashboard')) {
      setLanding('advanced-dashboard');
      handleMarketSidebarClick('advance');
      handleSidebarListItemClick(12);
    }
    if (window.location.pathname.includes('search-listing')) {
      setLanding('search-listing');
      handleMarketSidebarClick('searchListing');
    }
  }, [window.location.pathname, window.location.search]);

  const setTailorRegionLogic = (isTailored: string) => {
    if (isTailored) {
      setIsTailoredRegionOpen(true);
    }
  };

  useEffect(() => setTailorRegionLogic(isTailored));

  useEffect(() => {
    if (rateLimitAlert && isUserVerified) {
      handleOnSampleClick('expert');
    }
  }, [rateLimitAlert, isUserVerified]);

  // dashboard logic

  const resetCurrencyToDefault = () => {
    setCurrency(getUserPreferredCurrency(currency));
  };

  const closeBookingForm = () => {
    setShowDemoBookingForm(false);
  };

  const openDemoBookingForm = () => {
    setShowDemoBookingForm(true);
  };

  const checkGoogleAuthUserDescriptionFormStatus = () => {
    const userDescriptionFormStatus = localStorage.getItem('user-description-form-to-fill');
    if (userDescriptionFormStatus === 'true') {
      setShowOnboardingModal(true);
    }
  };

  const handleMarketSidebarClick = async (event: string) => {
    let emptyState = {
      showInsightsDashboard: false,
      showEstimatorDashboard: false,
      showMyAnalysisDashboard: false,
      showSettingsDashboard: false,
      showDynamicPricingDashboard: false,
      showTailoredRegion: false,
      showAdvancedDashboard: false,
      showSearchListingDashboard: false,
    };

    if (event === 'main') {
      openOverviewOnboardingPage();
    }
    if (event === 'estimator') {
      emptyState['showEstimatorDashboard'] = true;
    }
    if (event === 'myAnalysis') {
      emptyState['showMyAnalysisDashboard'] = true;
      window.history.pushState({}, '', '/my-analysis');
    }
    if (event === 'settings') {
      emptyState['showSettingsDashboard'] = true;
      window.history.pushState({}, 'Settings', '/user/settings');
    }
    if (event === 'tailoredRegion') {
      emptyState['showTailoredRegion'] = true;
      setIsTailoredRegionOpen(true);
    }
    if (event === 'listings') {
      emptyState['showDynamicPricingDashboard'] = true;
      const hasUserVisitedDynamicPricing =
        localStorage.getItem('visited-dynamic-pricing') === 'true';
      if (user && user.email && user.email != 'Trial' && !hasUserVisitedDynamicPricing) {
        try {
          const returnedData = await postOnSlack(
            'dynamic-pricing-monitor',
            `${user.email} checked out Dynamic Pricing.\n`
          );
          if (returnedData) {
            window.localStorage.setItem('visited-dynamic-pricing', 'true');
          }
        } catch (err) {
          console.log(err);
        }
      }
    }

    if (event === 'estimator') {
      window.history.pushState(
        {
          address_full: estimatorAddressQuery ? estimatorAddressQuery : estimatorAddressFullQuery,
          address_short: estimatorAddressFshortQuery,
          bedrooms: estimatorBedroomsQuery,
          report_type: estimatorReportTypeQuery,
          property_price: estimatorPropertyPriceQuery,
          monthly_rent: estimatorMontlyRentQuery,
        },
        '',
        // '/airbnb-calculator'
        window.location.pathname.includes('airbnb-calculator')
          ? window.location.pathname
          : '/airbnb-calculator'
      );
    } else if (event === 'listings') {
      window.history.pushState({}, '', '/pricing');
    } else if (event === 'tailoredRegion') {
      window.history.pushState({}, 'TailoredRegion', '/tailored/t');
    } else if (event === 'discovery') {
      emptyState['showInsightsDashboard'] = true;
      setOverviewDashboardLayoutView('discovery');
    } else if (event === 'compare') {
      emptyState['showInsightsDashboard'] = true;
      setOverviewDashboardLayoutView('compare');
      history.push('/markets/compare');
    } else if (event === 'advance') {
      emptyState['showAdvancedDashboard'] = true;
      window.history.pushState({}, '', '/advanced-dashboard');
      setOverviewDashboardLayoutView('advance');
    } else if (event === 'searchListing') {
      emptyState['showSearchListingDashboard'] = true;
      setOverviewDashboardLayoutView('searchListing');
    } else if (
      event !== 'settings' &&
      event !== 'myAnalysis' &&
      event !== 'discovery' &&
      event !== 'compare'
    ) {
      window.history.pushState({}, '', '/airbnb-data/landing');
    }
    setdashboardState(emptyState);
  };

  const handleSidebarListItemClick = (index: number) => {
    setSelectedSidebarIndex(index);
  };

  if (!country) {
    country = '';
  }

  // Onboarding
  const { isHighlight, highlightElement, removeHighlightElement } = useHighlight();

  const OnBoardingSearchBox = (
    source: string,
    showBasicSearchbar: boolean = false,
    handleCustomOnSearchChange: any = null,
    searchbarStyle: 'main' | 'compare' = 'main',
    searchbarType: 'main' | 'compare' = 'main'
  ) => (
    <MarketSearchBar
      search={search}
      showAlert={() => {}}
      notShowAlert={() => {}}
      setdashboardState={setdashboardState}
      user={user}
      district={district}
      isUserVerified={isUserVerified}
      rateLimitAlert={rateLimitAlert}
      setRateLimitAlert={setRateLimitAlert}
      isOnboardingOn={true}
      isMobileScreen={matches}
      cachedSearchMarkets={{ ...cachedSearchMarkets, userPrefCountryCode: searchBaseCC }}
      source={source}
      showBasicSearchbar={showBasicSearchbar}
      handleCustomOnSearchChange={handleCustomOnSearchChange}
      searchbarStyle={searchbarStyle}
      searchbarType={searchbarType}
      cachedTop10MarketCountryWise={cachedTop10MarketCountryWise}
    />
  );

  // SEO

  let SEODistrictName = isFilterChange ? search.data.name : district;
  let SEOCountryName = isFilterChange ? search.data.country : country;
  let SEOCityName = cityName && cityName != '0' ? ', ' + cityName : '';
  let SEOrecommendPageURL0 =
    'https://app.airbtics.com/airbnb-data/united-states/ca/san%20francisco';
  let SEOrecommendPageURL1 = 'https://app.airbtics.com/airbnb-data/united-states/fl/miami';
  let SEOrecommendPageDistrictName0 = 'San Francisco';
  let SEOrecommendPageDistrictName1 = 'Miami';
  try {
    if (search && search.data && search.data.recommendPageURLs) {
      SEOrecommendPageURL0 = search.data.recommendPageURLs[0];
      SEOrecommendPageDistrictName0 = SEOrecommendPageURL0.split('/').pop() || '';
      if (search.data.recommendPageURLs.length > 1) {
        SEOrecommendPageURL1 = search.data.recommendPageURLs[1];
        SEOrecommendPageDistrictName1 = SEOrecommendPageURL1.split('/').pop() || '';
      }
    }
  } catch (error) {
    console.log('Error while getting recommend page for SEO ', error);
  }

  const appBarHeight =
    ((theme.mixins.toolbar.minHeight ? theme.mixins.toolbar.minHeight : 0) as number) - 10;

  const spacingBottomForMobileSidebar = matches ? { paddingBottom: '80px' } : {};

  const showHeaderOrBottomBarOnMobileView = overviewDashboardLayoutView !== 'map' || !matches;

  const getOverviewLandingPageComponent = () => {
    return (
      <OverviewLandingPage
        SearchBox={OnBoardingSearchBox}
        handleMarketSidebarClick={handleMarketSidebarClick}
        dummyMarket={dummyMarket}
        handleOnSampleClick={handleOnSampleClick}
        currency={currency}
        resetCurrencyToDefault={resetCurrencyToDefault}
        isLoggedIn={isLoggedIn}
      />
    );
  };

  const getEstimatorDashboardComponent = (searchbarOnly: boolean = false) => {
    return (
      <EstimatorPage
        setSubscriptionModalOpen={setSubscriptionModalOpen}
        user={user}
        isLoggedIn={isLoggedIn}
        loginModalOpen={loginModalOpen}
        setLoginModalOpen={setLoginModalOpen}
        matches={matches}
        currency={currency}
        setIsEmailVerifyModalOpen={setIsEmailVerifyModalOpen}
        googleAutocompleteSessionToken={googleAutocompleteSessionToken}
        getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
        clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
        googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
        dummyMarket={dummyMarketEstimator}
        dummyOverviewMarket={dummyMarket}
        loginCountryCode={loginCountryCode}
        setEstimatorSimplePricingModalViewMode={setRequiredSubscriptionPlan}
        setShowTrial={setShowTrial}
        handleOnSampleClick={handleOnSampleClick}
        resetCurrencyToDefault={resetCurrencyToDefault}
        OnBoardingSearchBox={OnBoardingSearchBox}
        openComparePage={openReportComparePage}
        setOpenComparePage={setOpenReportComparePage}
        searchbarOnly={searchbarOnly}
      />
    );
  };

  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [tutorialType, setTutorialType] = useState<'market_research' | 'neighborhood_research'>(
    'market_research'
  );

  return (
    <>
      <a
        href={SEOrecommendPageURL0}
        className={classes.SEOLinkStyle}
        style={{
          marginTop: isMobile ? '-40px' : 0,
        }}
      >
        {' '}
        {`See short-term rental data in ${SEOrecommendPageDistrictName0}`}{' '}
      </a>
      <a
        href={SEOrecommendPageURL1}
        className={classes.SEOLinkStyle}
        style={{
          marginTop: isMobile ? '-40px' : 0,
        }}
      >
        {' '}
        {`See Airbnb occupancy, daily rate and revenue data in ${SEOrecommendPageDistrictName1}`}{' '}
      </a>

      {rateLimitAlert && !isUserVerified && user.isSubscribed === false && (
        <ViewDemoButton
          handleClick={() => setIsEmailVerifyModalOpen(true)}
          buttonText="You have used up all 3 free searches. Please verify your email to get free searches."
          topOffsetMobile="30%"
        />
      )}

      <DemoBookingForm
        matches={matches}
        user={user}
        showDemoBookingForm={showDemoBookingForm}
        closeBookingForm={closeBookingForm}
        headingFor={demoFormHeadingFor}
      />

      {showHeaderOrBottomBarOnMobileView && (
        <Header
          setLoginModalOpen={setLoginModalOpen}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          dashboardState={dashboardState}
          setdashboardState={setdashboardState}
          isMobile={matches}
          handleMarketSidebarClick={handleMarketSidebarClick}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          district={district}
          isUserVerified={isUserVerified}
          rateLimitAlert={rateLimitAlert}
          setRateLimitAlert={setRateLimitAlert}
          cachedSearchMarkets={{ ...cachedSearchMarkets, userPrefCountryCode: searchBaseCC }}
          handleSidebarListItemClick={handleSidebarListItemClick}
          centerCoord={centerCoord}
          address={address}
          dashboardLayoutView={overviewDashboardLayoutView}
          openDemoBookingForm={openDemoBookingForm}
          setDemoFormHeadingFor={setDemoFormHeadingFor}
          dummyMarket={dummyMarket}
        />
      )}

      <Sidebar
        parent="market"
        handleSidebarClick={handleMarketSidebarClick}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        matches={matches}
        selectedSidebarIndex={selectedSidebarIndex}
        handleSidebarListItemClick={handleSidebarListItemClick}
        currency={currency}
        setCurrency={setCurrency}
        isUserVerified={isUserVerified}
        setIsEmailVerifyModalOpen={setIsEmailVerifyModalOpen}
        dashboardState={dashboardState}
        user={user}
        isLoggedIn={isLoggedIn}
        showHeaderOrBottomBarOnMobileView={showHeaderOrBottomBarOnMobileView}
        setOpenReportComparePage={setOpenReportComparePage}
      />

      {loginModalOpen && !isLoggedIn && (
        <AuthModal
          authModalOpen={loginModalOpen}
          setAuthModalOpen={setLoginModalOpen}
          getUserInfo={getUserInfo}
          getTokenThunk={getTokenThunk}
          matches={matches}
          landing={landing}
          setShowOnboardingModal={setShowOnboardingModal}
        />
      )}

      {showOnboardingModal && (
        <UserOnboardingModal setShowOnboardingModal={setShowOnboardingModal} />
      )}

      {subscriptionModalOpen && (
        <SubscriptionModal
          countryCode={search.data.countryCode}
          city={search.data.city}
          district={search.data.name}
          getTokenThunk={getTokenThunk}
          subscriptionModalOpen={subscriptionModalOpen}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          setIsTailoredRegionOpen={setIsTailoredRegionOpen}
          user={user}
          setShowUpgradeSubscriptionModal={setShowUpgradeSubscriptionModal}
          basicViewPlanName={requiredSubscriptionPlan}
          view={subscriptionModalView}
          setView={setSubscriptionModalView}
          origin={subscriptionModalOrigin}
          showTrial={showTrial}
          setRequiredSubscriptionPlan={setRequiredSubscriptionPlan}
          setSubscriptionModalView={setSubscriptionModalView}
          setSubscriptionModalOrigin={setSubscriptionModalOrigin}
          resetSubscriptionModal={resetSubscriptionModal}
        />
      )}

      {showUpgradeSubscriptionModal && (
        <UpgradeSubscriptionModal
          plan={showUpgradeSubscriptionModal}
          setPlan={setShowUpgradeSubscriptionModal}
          resetSubscriptionModal={resetSubscriptionModal}
        />
      )}

      {showUpgradeYearlySubscriptionModal && (
        <UpgradeYearlyPlanModal
          user={user}
          setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
        />
      )}

      {(isOverviewLanding || isIndividualLandingPage || isIndividualHostPage) && (
        <>
          <main
            className={clsx(classes.root, {
              [classes.content]: sidebarOpen,
              [classes.contentShift]: !sidebarOpen,
            })}
            style={spacingBottomForMobileSidebar}
          >
            <Container
              className={classes.container}
              style={{
                paddingTop: appBarHeight,
                maxWidth: 4600,
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 0,
              }}
            >
              {getOverviewLandingPageComponent()}

              {isIndividualLandingPage && (
                <IndividualListingPage
                  user={user}
                  currency={currency}
                  isLoggedIn={isLoggedIn}
                  subscriptionModalOpen={subscriptionModalOpen}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  handleOnSampleClick={handleOnSampleClick}
                  openDemoBookingForm={openDemoBookingForm}
                  setDemoFormHeadingFor={setDemoFormHeadingFor}
                />
              )}

              {isIndividualHostPage && (
                <IndividualHostPage
                  user={user}
                  currency={currency}
                  isLoggedIn={isLoggedIn}
                  subscriptionModalOpen={subscriptionModalOpen}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  handleOnSampleClick={handleOnSampleClick}
                  isMobileScreen={matches}
                />
              )}
            </Container>
          </main>
        </>
      )}

      {!isHomePage && !isOverviewLanding && !isIndividualLandingPage && !isIndividualHostPage && (
        <>
          <main
            className={clsx(classes.root, {
              [classes.content]: sidebarOpen,
              [classes.contentShift]: !sidebarOpen,
            })}
            style={{
              ...(!showHeaderOrBottomBarOnMobileView && { marginTop: `-${HEADER_HEIGHT}` }),
            }}
            id="market-dashboard-main"
          >
            <Container
              className={classes.container}
              style={{
                paddingTop: appBarHeight,
                maxWidth: 4600,
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 0,
              }}
            >
              {dashboardState.showInsightsDashboard && (
                <OverviewPage
                  search={search}
                  SEODistrictName={SEODistrictName}
                  SEOCountryName={SEOCountryName}
                  SEOCityName={SEOCityName}
                  dashboardLayoutView={overviewDashboardLayoutView}
                  setDashboardLayoutView={setOverviewDashboardLayoutView}
                  matches={matches}
                  subscriptionModalOpen={subscriptionModalOpen}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  currency={currency}
                  user={user}
                  showSearchCreditDeductWarningModal={showSearchCreditDeductWarningModal}
                  setShowSearchCreditDeductWarningModal={setShowSearchCreditDeductWarningModal}
                  isLoggedIn={isLoggedIn}
                  handleOnSampleClick={handleOnSampleClick}
                  setSelectedSidebarIndex={setSelectedSidebarIndex}
                  handleMarketSidebarClick={handleMarketSidebarClick}
                  handleSidebarListItemClick={handleSidebarListItemClick}
                  setAdvancedTab={setAdvancedTab}
                  openDemoBookingForm={openDemoBookingForm}
                  setDemoFormHeadingFor={setDemoFormHeadingFor}
                  dummyMarket={dummyMarket}
                  loginCountryCode={loginCountryCode}
                  dataStoreTimePeriodQuery={dataStoreTimePeriodQuery}
                  dataStoreTimeGranularityQuery={dataStoreTimeGranularityQuery}
                  getTokenThunk={getTokenThunk}
                  tailoredRegionMarkers={tailoredRegionMarkers}
                  getTailoredRegionMarkers={getTailoredRegionMarkers}
                  OnBoardingSearchBox={OnBoardingSearchBox}
                  showHeaderOrBottomBarOnMobileView={showHeaderOrBottomBarOnMobileView}
                  cachedMarketData={cachedMarketData}
                  getEstimatorDashboardComponent={getEstimatorDashboardComponent}
                />
              )}

              {dashboardState.showEstimatorDashboard && getEstimatorDashboardComponent()}
              {dashboardState.showMyAnalysisDashboard && (
                <MyAnalysisDashboard user={user} getUserInfo={getUserInfo} matches={matches} />
              )}
              {dashboardState.showSettingsDashboard && (
                <SettingsDashboardV2
                  user={user}
                  isMobileView={matches}
                  setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
                  currency={currency}
                  setCurrency={setCurrency}
                  OverviewLandingPageComponent={getOverviewLandingPageComponent()}
                  EstimatorDashboardComponent={getEstimatorDashboardComponent()}
                  isLoggedIn={isLoggedIn}
                  setLoginModalOpen={setLoginModalOpen}
                  isMobileScreen={matches}
                  resetCurrencyToDefault={resetCurrencyToDefault}
                />
              )}
              {dashboardState.showDynamicPricingDashboard && (
                <DynamicPricingDashboard matches={matches} />
              )}
              {dashboardState.showAdvancedDashboard && (
                <AdvancedDashboard
                  matches={matches}
                  user={user}
                  advancedTab={advancedTab}
                  setAdvancedTab={setAdvancedTab}
                  dummyMarket={dummyMarket}
                  handleOnSampleClick={handleOnSampleClick}
                  resetCurrencyToDefault={resetCurrencyToDefault}
                />
              )}
              {dashboardState.showSearchListingDashboard && (
                <SearchListings
                  currency={currency}
                  resetCurrencyToDefault={resetCurrencyToDefault}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  handleOnSampleClick={handleOnSampleClick}
                  subscriptionModalOpen={subscriptionModalOpen}
                  openDemoBookingForm={openDemoBookingForm}
                  setDemoFormHeadingFor={setDemoFormHeadingFor}
                  user={user}
                  matches={matches}
                />
              )}
            </Container>
          </main>
        </>
      )}

      {isTailoredRegionOpen && (
        <OnboardingModalMobile
          getTokenThunk={getTokenThunk}
          setIsTailoredRegionOpen={setIsTailoredRegionOpen}
          tailoredRegionMarkers={tailoredRegionMarkers}
          getTailoredRegionMarkers={getTailoredRegionMarkers}
        />
      )}

      {isLoggedIn && isEmailVerifyModalOpen && !user.isVerified && !user.loading && (
        <EmailVerifyModal
          open={isEmailVerifyModalOpen}
          setOpen={setIsEmailVerifyModalOpen}
          email={user.email}
          matches={matches}
        />
      )}

      {showTutorial && (
        <TutorialModal open={showTutorial} onClose={handleTutorialClose} type={tutorialType} />
      )}
    </>
  );
};

MarketDashboard.propTypes = {
  getMarkers: PropTypes.func,
  getGraphData: PropTypes.func,
  getSearch: PropTypes.func,
  searchChange: PropTypes.func,
  getTokenThunk: PropTypes.func,
  getUserInfo: PropTypes.func,
  getStatsPerADR: PropTypes.func,
  getBookingChanges: PropTypes.func,
};

export default MarketDashboard;
